import { inject } from '@angular/core';
import {
  CanActivateChildFn,
  CanActivateFn,
  Router,
  UrlTree,
} from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { from, map, Observable, of, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';

const tryRefreshThenLogin = (oauthService: OAuthService): Observable<boolean> => {
  return from(oauthService.refreshToken()).pipe(
    switchMap(() => {
      if (oauthService.hasValidAccessToken()) {
        return of(true);
      }

      oauthService.loadDiscoveryDocumentAndLogin();
      return of(false);
    }),
    catchError(() => {
      oauthService.loadDiscoveryDocumentAndLogin();
      return of(false);
    }),
  );
};

const redirectToLogin = (router: Router, returnUrl: string): UrlTree =>
  router.createUrlTree(['/login'], { queryParams: { returnUrl } });

export const authGuardNative: CanActivateFn = (_route, state) => {
  const oauth = inject(OAuthService);
  const router = inject(Router);

  if (oauth.hasValidAccessToken()) {
    return true;
  }

  return from(oauth.refreshToken()).pipe(
    switchMap(() => of(oauth.hasValidAccessToken())),
    map((ok) => (ok ? true : redirectToLogin(router, state.url))),
    catchError(() => of(redirectToLogin(router, state.url))),
  );
};

export const authGuard: CanActivateFn = () => {
  const oauthService: OAuthService = inject(OAuthService);

  if (oauthService.hasValidAccessToken()) {
    return true;
  }

  return tryRefreshThenLogin(oauthService);
};

export const authGuardChild: CanActivateChildFn = () => {
  const oauthService: OAuthService = inject(OAuthService);

  if (oauthService.hasValidAccessToken()) {
    return true;
  }

  return tryRefreshThenLogin(oauthService);
};
